import {Component, inject} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {RouterLink, RouterLinkActive} from '@angular/router';
import {NgbDropdown, NgbDropdownItem, NgbDropdownMenu, NgbDropdownToggle} from '@ng-bootstrap/ng-bootstrap';
import {map, of, switchMap} from 'rxjs';
import {OidcSecurityService} from 'angular-auth-oidc-client';
import {environment} from '../../../../environments/environment';
import {tap} from 'rxjs/operators';
import {FloatingChatComponent} from '../../../views/floating-chat/floating-chat.component';
import {logoutWithFallback} from '../../../shared/auth/logout';
import {restoreSession} from '../../../shared/auth/restore-session';
import {hasAnyRoleInSet, loadUserRoles, MENU_ROLES} from '../../../shared/auth/role-access';

type MyUserData = any;

@Component({
  selector: 'app-public-nav',
  standalone: true,
  imports: [
    RouterLink,
    RouterLinkActive,
    NgbDropdown,
    NgbDropdownToggle,
    NgbDropdownMenu,
    NgbDropdownItem,
    FloatingChatComponent
  ],
  templateUrl: './public-nav.component.html',
  styleUrl: './public-nav.component.scss'
})
export class PublicNavComponent {
  private readonly oidcSecurityService = inject(OidcSecurityService);

  /** หน้าแรก = landing ของ gateway ตาม environment (stg: gateway.ecoding.acfs.geniustree.io, prod: gwecoding.acfs.go.th) */
  protected readonly homeUrl = `${environment.gatewayUrl}/?acfs_landing=1`;
  /** สมัครสมาชิกทำที่ portal ของแต่ละ environment (stg: portal.acfs.geniustree.io, prod: portal.acfs.go.th). */
  protected readonly registerUrl = `${environment.portalConsoleUrl.replace(/\/+$/, '')}/ui/public/consent`;
  protected menuOpen = false;

  protected readonly isAuthenticated = toSignal(
    this.oidcSecurityService.isAuthenticated$
      .pipe(
        map(({isAuthenticated}) => isAuthenticated)
      ),
    {initialValue: false}
  );

  /** เจ้าหน้าที่ (SUPER_ADMIN / ADMIN / OFFICER / ACFS_OFFICER) เห็นเมนู "สำหรับเจ้าหน้าที่" ไปหน้ารายการรอตรวจสอบ */
  protected readonly isStaff = toSignal(
    this.oidcSecurityService.isAuthenticated$.pipe(
      switchMap(({isAuthenticated}) => isAuthenticated
        ? loadUserRoles(this.oidcSecurityService)
        : of(new Set<string>())),
      map(roles => hasAnyRoleInSet(roles, MENU_ROLES.certificateWaitCheck)),
    ),
    {initialValue: false}
  );

  protected readonly user = toSignal<MyUserData>(
    this.oidcSecurityService.userData$
      .pipe(
        tap(({userData}) => console.log(userData)),
        map(({userData}) => userData as MyUserData)
      ),
    {initialValue: null}
  );

  constructor() {
    // หน้าสาธารณะข้าม checkAuth ตอน bootstrap (app.config) — ถ้า access token หมดอายุแล้ว
    // ต้องต่ออายุด้วย refresh token เอง ไม่งั้นเมนูจะแสดง "เข้าสู่ระบบ" ทั้งที่ยัง login อยู่
    restoreSession(this.oidcSecurityService, {
      onFailure: result => {
        // ต่ออายุไม่ได้ (refresh token หมดอายุ) — ล้าง token เก่าในแท็บนี้ แต่ไม่พาไปหน้า 401 บนหน้าสาธารณะ
        this.oidcSecurityService.logoffLocal();
        return of({...result, isAuthenticated: false, accessToken: '', idToken: '', userData: null});
      },
    }).subscribe();
  }

  protected getInitial(word: string | undefined): string {
    if (!word) {
      return '';
    }
    return 'เแโใไ'.includes(word[0]) ? (word[1] ?? '') : word[0];
  }

  login(): void {
    this.oidcSecurityService.authorize();
  }

  private redirectTo(url: string): void {
    window.location.assign(url);
  }

  logout(): void {
    logoutWithFallback(this.oidcSecurityService, url => this.redirectTo(url));
  }

}
